import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Calendar } from 'primereact/calendar';
import { Button } from 'primereact/button';
import { Message } from 'primereact/message';
import { ProgressSpinner } from 'primereact/progressspinner';
import { Dialog } from 'primereact/dialog';
import { InputText } from 'primereact/inputtext';
import { Dropdown } from 'primereact/dropdown';
import { DataTable } from 'primereact/datatable';
import { Column } from 'primereact/column';
import { TabView, TabPanel } from 'primereact/tabview';
import * as XLSX from 'xlsx';
import { jsPDF } from 'jspdf';
import autoTable from 'jspdf-autotable';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:3006/api';

function TaskManagement() {
    // Active task panel
    const [activeTask, setActiveTask] = useState(null);
    
    // Attendance Sync states
    const [dateFrom, setDateFrom] = useState(new Date());
    const [dateTo, setDateTo] = useState(new Date());
    const [syncLoading, setSyncLoading] = useState(false);
    const [syncResult, setSyncResult] = useState(null);
    const [syncError, setSyncError] = useState(null);
    
    // Purge TID states
    const [purgeDialogVisible, setPurgeDialogVisible] = useState(false);
    const [purgePin, setPurgePin] = useState('');
    const [purgeLoading, setPurgeLoading] = useState(false);
    const [purgeResult, setPurgeResult] = useState(null);
    const [purgeError, setPurgeError] = useState(null);

    // Download Reports states
    const [reportMonth, setReportMonth] = useState(new Date());
    const [teacherTypes, setTeacherTypes] = useState([]);
    const [selectedTeacherType, setSelectedTeacherType] = useState(null);
    const [reportLoading, setReportLoading] = useState(false);
    const [searchQuery, setSearchQuery] = useState('');
    const [searchResults, setSearchResults] = useState([]);
    const [searchLoading, setSearchLoading] = useState(false);
    const [selectedTeacher, setSelectedTeacher] = useState(null);
    const debounceRef = useRef(null);
    
    // Report preview states
    const [previewData, setPreviewData] = useState([]);
    const [previewVisible, setPreviewVisible] = useState(false);
    const [previewTitle, setPreviewTitle] = useState('');
    const [generatingVisible, setGeneratingVisible] = useState(false);
    
    // Individual report preview states
    const [individualPreviewData, setIndividualPreviewData] = useState([]);
    const [individualPreviewVisible, setIndividualPreviewVisible] = useState(false);
    const [individualTeacherInfo, setIndividualTeacherInfo] = useState(null);

    // Teacher registration states
    const [regForm, setRegForm] = useState({
        firstName: '', lastName: '', nic: '', title: 'Mr.',
        address1: '', address2: '', address3: '',
        mobile: '', whatsapp: '', email: '', dob: null, teacherTypeId: null
    });
    const [regLoading, setRegLoading] = useState(false);
    const [regResult, setRegResult] = useState(null);
    const [regError, setRegError] = useState(null);

    // Staff registration states
    const [staffForm, setStaffForm] = useState({
        firstName: '', lastName: '', nic: '', title: 'Mr.',
        address1: '', address2: '', address3: '',
        mobile: '', whatsapp: '', email: '', dob: null,
        designation: '', epfNo: ''
    });
    const [staffLoading, setStaffLoading] = useState(false);
    const [staffResult, setStaffResult] = useState(null);
    const [staffError, setStaffError] = useState(null);

    // Manual attendance adjustment states
    const [adjustForm, setAdjustForm] = useState({
        teacherId: null, date: null, inTime: '', outTime: '', remarks: ''
    });
    const [adjustLoading, setAdjustLoading] = useState(false);
    const [adjustResult, setAdjustResult] = useState(null);
    const [adjustError, setAdjustError] = useState(null);
    const [adjustTeacherSearch, setAdjustTeacherSearch] = useState('');
    const [adjustTeacherResults, setAdjustTeacherResults] = useState([]);
    const [selectedAdjustTeacher, setSelectedAdjustTeacher] = useState(null);

    // Verification states
    const [pendingEntries, setPendingEntries] = useState([]);
    const [verifyLoading, setVerifyLoading] = useState(false);
    const [verifyError, setVerifyError] = useState(null);

    // Fetch teacher types on mount
    useEffect(() => {
        const fetchTeacherTypes = async () => {
            try {
                const response = await fetch(`${API_BASE_URL}/teacher-types`);
                const data = await response.json();
                if (data.success) {
                    setTeacherTypes([{ id: null, name: 'All Types' }, ...data.types]);
                }
            } catch (err) {
                console.error('Failed to fetch teacher types:', err);
            }
        };
        fetchTeacherTypes();
    }, []);

    // Debounced teacher search
    const searchTeachers = useCallback(async (query) => {
        if (!query || query.length < 2) {
            setSearchResults([]);
            return;
        }
        setSearchLoading(true);
        try {
            const token = localStorage.getItem('adminToken');
            const response = await fetch(`${API_BASE_URL}/teacher/search?q=${encodeURIComponent(query)}`, {
                headers: { 'Authorization': `Bearer ${token}` }
            });
            const data = await response.json();
            if (data.success) {
                setSearchResults(data.teachers || []);
            }
        } catch (err) {
            console.error('Search error:', err);
        } finally {
            setSearchLoading(false);
        }
    }, []);

    // Handle search input with debounce
    const handleSearchChange = (value) => {
        setSearchQuery(value);
        setSelectedTeacher(null);
        
        if (debounceRef.current) {
            clearTimeout(debounceRef.current);
        }
        
        debounceRef.current = setTimeout(() => {
            searchTeachers(value);
        }, 400);
    };

    // Preview monthly report
    const handlePreviewMonthlyReport = async () => {
        setGeneratingVisible(true);
        setReportLoading(true);
        try {
            const token = localStorage.getItem('adminToken');
            const year = reportMonth.getFullYear();
            const month = reportMonth.getMonth() + 1;
            const typeParam = selectedTeacherType ? `&typeId=${selectedTeacherType}` : '';
            
            const response = await fetch(
                `${API_BASE_URL}/reports/monthly-attendance?year=${year}&month=${month}${typeParam}`,
                { headers: { 'Authorization': `Bearer ${token}` } }
            );
            
            if (response.ok) {
                const result = await response.json();
                if (result.success && result.data) {
                    setPreviewData(result.data);
                    const monthName = reportMonth.toLocaleString('default', { month: 'long' });
                    setPreviewTitle(`Monthly Attendance - ${monthName} ${year}`);
                    setGeneratingVisible(false);
                    setPreviewVisible(true);
                }
            }
        } catch (err) {
            console.error('Preview error:', err);
            setGeneratingVisible(false);
        } finally {
            setReportLoading(false);
        }
    };

    // Download monthly report from preview
    const handleDownloadMonthlyReport = () => {
        const year = reportMonth.getFullYear();
        const month = reportMonth.getMonth() + 1;
        const wsData = previewData.map(row => ({
            'TID': row.tid,
            'Name': row.name,
            'Teacher Type': row.teacherType || '',
            'NIC': row.nic || '',
            'Working Days': row.workingDays || 0,
            'Present Days': row.presentDays,
            'Absent Days': row.absentDays
        }));
        const ws = XLSX.utils.json_to_sheet(wsData);
        const wb = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(wb, ws, 'Attendance');
        XLSX.writeFile(wb, `attendance_${year}_${String(month).padStart(2, '0')}.xlsx`);
    };

    // Download monthly report as PDF
    const handleDownloadMonthlyPDF = () => {
        const year = reportMonth.getFullYear();
        const month = reportMonth.getMonth() + 1;
        const monthName = reportMonth.toLocaleString('default', { month: 'long' });
        
        const doc = new jsPDF();
        
        // Title
        doc.setFontSize(16);
        doc.setFont('helvetica', 'bold');
        doc.text('Monthly Attendance Report', 105, 15, { align: 'center' });
        
        // Subtitle
        doc.setFontSize(12);
        doc.setFont('helvetica', 'normal');
        doc.text(`${monthName} ${year}`, 105, 23, { align: 'center' });
        
        // Summary
        doc.setFontSize(10);
        const totalPresent = previewData.reduce((sum, r) => sum + (r.presentDays || 0), 0);
        doc.text(`Total Teachers: ${previewData.length}  |  Total Present Days: ${totalPresent}`, 14, 32);
        
        // Table
        autoTable(doc, {
            startY: 38,
            head: [['TID', 'Name', 'Type', 'NIC', 'Working', 'Present', 'Absent']],
            body: previewData.map(row => [
                row.tid,
                row.name,
                row.teacherType || '-',
                row.nic || '-',
                row.workingDays || 0,
                row.presentDays,
                row.absentDays
            ]),
            styles: { fontSize: 8, cellPadding: 2 },
            headStyles: { fillColor: [59, 130, 246], textColor: 255 },
            alternateRowStyles: { fillColor: [248, 250, 252] },
            columnStyles: {
                0: { cellWidth: 15 },
                1: { cellWidth: 45 },
                2: { cellWidth: 30 },
                3: { cellWidth: 30 },
                4: { cellWidth: 18, halign: 'center' },
                5: { cellWidth: 18, halign: 'center' },
                6: { cellWidth: 18, halign: 'center' }
            }
        });
        
        // Footer
        const pageCount = doc.internal.getNumberOfPages();
        for (let i = 1; i <= pageCount; i++) {
            doc.setPage(i);
            doc.setFontSize(8);
            doc.setTextColor(128);
            doc.text(`Generated on ${new Date().toLocaleString()}`, 14, doc.internal.pageSize.height - 10);
            doc.text(`Page ${i} of ${pageCount}`, doc.internal.pageSize.width - 25, doc.internal.pageSize.height - 10);
        }
        
        const pdfUrl = doc.output('bloburl');
        window.open(pdfUrl, '_blank');
    };

    // Preview individual report
    const handlePreviewIndividualReport = async () => {
        if (!selectedTeacher) return;
        setGeneratingVisible(true);
        setReportLoading(true);
        try {
            const token = localStorage.getItem('adminToken');
            const year = reportMonth.getFullYear();
            const month = reportMonth.getMonth() + 1;
            
            const response = await fetch(
                `${API_BASE_URL}/reports/individual-attendance?teacherId=${selectedTeacher.id}&year=${year}&month=${month}`,
                { headers: { 'Authorization': `Bearer ${token}` } }
            );
            
            if (response.ok) {
                const result = await response.json();
                if (result.success) {
                    setIndividualTeacherInfo(result.teacher);
                    setIndividualPreviewData(result.attendance);
                    setGeneratingVisible(false);
                    setIndividualPreviewVisible(true);
                }
            }
        } catch (err) {
            console.error('Preview error:', err);
            setGeneratingVisible(false);
        } finally {
            setReportLoading(false);
        }
    };

    // Download individual report from preview
    const handleDownloadIndividualReport = () => {
        if (!individualTeacherInfo || !individualPreviewData.length) return;
        
        const year = reportMonth.getFullYear();
        const month = reportMonth.getMonth() + 1;
        
        const wsData = individualPreviewData.map(row => ({
            'Date': row.date,
            'Day': row.dayName,
            'In Time': row.inTime || '-',
            'Out Time': row.outTime || '-'
        }));
        const ws = XLSX.utils.json_to_sheet(wsData);
        
        // Add teacher info header
        XLSX.utils.sheet_add_aoa(ws, [
            [`Teacher: ${individualTeacherInfo.name} (TID: ${individualTeacherInfo.tid})`],
            [`NIC: ${individualTeacherInfo.nic || 'N/A'}`],
            [`Month: ${year}-${String(month).padStart(2, '0')}`],
            []
        ], { origin: 'A1' });
        
        const wb = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(wb, ws, 'Attendance');
        XLSX.writeFile(wb, `attendance_${individualTeacherInfo.tid}_${year}_${String(month).padStart(2, '0')}.xlsx`);
    };

    // Download individual report as PDF
    const handleDownloadIndividualPDF = () => {
        if (!individualTeacherInfo || !individualPreviewData.length) return;
        
        const year = reportMonth.getFullYear();
        const month = reportMonth.getMonth() + 1;
        const monthName = reportMonth.toLocaleString('default', { month: 'long' });
        
        const doc = new jsPDF();
        
        // Title
        doc.setFontSize(16);
        doc.setFont('helvetica', 'bold');
        doc.text('Individual Attendance Report', 105, 15, { align: 'center' });
        
        // Teacher Info Box
        doc.setFontSize(11);
        doc.setFont('helvetica', 'normal');
        doc.setFillColor(248, 250, 252);
        doc.rect(14, 22, 182, 24, 'F');
        doc.text(`Teacher: ${individualTeacherInfo.name}`, 18, 30);
        doc.text(`TID: ${individualTeacherInfo.tid}  |  NIC: ${individualTeacherInfo.nic || 'N/A'}`, 18, 37);
        doc.text(`Type: ${individualTeacherInfo.teacherType || 'N/A'}  |  Month: ${monthName} ${year}`, 18, 44);
        
        // Summary
        doc.setFontSize(10);
        const presentCount = individualPreviewData.filter(r => r.inTime).length;
        const absentCount = individualPreviewData.filter(r => !r.inTime && r.dayName !== 'Saturday' && r.dayName !== 'Sunday').length;
        doc.text(`Present: ${presentCount}  |  Absent: ${absentCount}  |  Total Days: ${individualPreviewData.length}`, 14, 54);
        
        // Table
        autoTable(doc, {
            startY: 60,
            head: [['Date', 'Day', 'In Time', 'Out Time', 'Status']],
            body: individualPreviewData.map(row => {
                const isWeekend = row.dayName === 'Saturday' || row.dayName === 'Sunday';
                const status = isWeekend ? 'Weekend' : (row.inTime ? 'Present' : 'Absent');
                return [
                    row.date,
                    row.dayName,
                    row.inTime || '-',
                    row.outTime || '-',
                    status
                ];
            }),
            styles: { fontSize: 9, cellPadding: 3 },
            headStyles: { fillColor: [59, 130, 246], textColor: 255 },
            bodyStyles: { valign: 'middle' },
            columnStyles: {
                0: { cellWidth: 30 },
                1: { cellWidth: 30 },
                2: { cellWidth: 30, halign: 'center' },
                3: { cellWidth: 30, halign: 'center' },
                4: { cellWidth: 30, halign: 'center' }
            },
            didParseCell: function(data) {
                if (data.section === 'body') {
                    const status = data.row.raw[4];
                    if (status === 'Weekend') {
                        data.cell.styles.fillColor = [243, 244, 246];
                        data.cell.styles.textColor = [156, 163, 175];
                    } else if (status === 'Absent') {
                        data.cell.styles.fillColor = [254, 242, 242];
                        data.cell.styles.textColor = [220, 38, 38];
                    }
                }
            }
        });
        
        // Footer
        doc.setFontSize(8);
        doc.setTextColor(128);
        doc.text(`Generated on ${new Date().toLocaleString()}`, 14, doc.internal.pageSize.height - 10);
        
        const pdfUrl = doc.output('bloburl');
        window.open(pdfUrl, '_blank');
    };

    // Handle staff registration
    const handleRegisterStaff = async () => {
        if (!staffForm.firstName || !staffForm.lastName || !staffForm.nic) {
            setStaffError('Please fill in all required fields (First Name, Last Name, NIC)');
            return;
        }
        
        setStaffLoading(true);
        setStaffResult(null);
        setStaffError(null);
        
        try {
            const token = localStorage.getItem('adminToken');
            const response = await fetch(`${API_BASE_URL}/staff/register`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${token}`
                },
                body: JSON.stringify({
                    ...staffForm,
                    dob: staffForm.dob ? formatDate(staffForm.dob) : null
                })
            });
            
            const data = await response.json();
            
            if (data.success) {
                setStaffResult(data);
                setStaffForm({
                    firstName: '', lastName: '', nic: '', title: 'Mr.',
                    address1: '', address2: '', address3: '',
                    mobile: '', whatsapp: '', email: '', dob: null,
                    designation: '', epfNo: ''
                });
            } else {
                setStaffError(data.message || 'Registration failed');
            }
        } catch (err) {
            setStaffError('Failed to register staff. Please try again.');
        } finally {
            setStaffLoading(false);
        }
    };

    // Search teachers for adjustment
    const handleAdjustTeacherSearch = async (query) => {
        setAdjustTeacherSearch(query);
        if (query.length < 2) {
            setAdjustTeacherResults([]);
            return;
        }
        try {
            const token = localStorage.getItem('adminToken');
            const response = await fetch(`${API_BASE_URL}/teachers/search?query=${encodeURIComponent(query)}`, {
                headers: { 'Authorization': `Bearer ${token}` }
            });
            const data = await response.json();
            if (data.success) {
                setAdjustTeacherResults(data.teachers);
            }
        } catch (err) {
            console.error('Teacher search error:', err);
        }
    };

    // Handle manual attendance adjustment
    const handleAdjustAttendance = async () => {
        if (!selectedAdjustTeacher || !adjustForm.date) {
            setAdjustError('Please select a teacher and date');
            return;
        }
        if (!adjustForm.inTime && !adjustForm.outTime) {
            setAdjustError('Please enter at least In Time or Out Time');
            return;
        }
        
        setAdjustLoading(true);
        setAdjustResult(null);
        setAdjustError(null);
        
        try {
            const token = localStorage.getItem('adminToken');
            const response = await fetch(`${API_BASE_URL}/attendance/adjust`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${token}`
                },
                body: JSON.stringify({
                    teacherId: selectedAdjustTeacher.id,
                    date: formatDate(adjustForm.date),
                    inTime: adjustForm.inTime || null,
                    outTime: adjustForm.outTime || null,
                    remarks: adjustForm.remarks
                })
            });
            
            const data = await response.json();
            
            if (data.success) {
                setAdjustResult(data);
                setAdjustForm({ teacherId: null, date: null, inTime: '', outTime: '', remarks: '' });
                setSelectedAdjustTeacher(null);
                setAdjustTeacherSearch('');
            } else {
                setAdjustError(data.message || 'Failed to adjust attendance');
            }
        } catch (err) {
            setAdjustError('Failed to adjust attendance. Please try again.');
        } finally {
            setAdjustLoading(false);
        }
    };

    // Fetch pending entries for verification
    const fetchPendingEntries = async () => {
        setVerifyLoading(true);
        setVerifyError(null);
        try {
            const token = localStorage.getItem('adminToken');
            const response = await fetch(`${API_BASE_URL}/attendance/pending`, {
                headers: { 'Authorization': `Bearer ${token}` }
            });
            const data = await response.json();
            if (data.success) {
                setPendingEntries(data.entries);
            } else {
                setVerifyError(data.message);
            }
        } catch (err) {
            setVerifyError('Failed to fetch pending entries');
        } finally {
            setVerifyLoading(false);
        }
    };

    // Handle verification (approve/reject)
    const handleVerifyEntry = async (entryId, action) => {
        try {
            const token = localStorage.getItem('adminToken');
            const response = await fetch(`${API_BASE_URL}/attendance/verify`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${token}`
                },
                body: JSON.stringify({ entryId, action })
            });
            
            const data = await response.json();
            if (data.success) {
                fetchPendingEntries();
            } else {
                setVerifyError(data.message);
            }
        } catch (err) {
            setVerifyError('Failed to verify entry');
        }
    };

    // Handle teacher registration
    const handleRegisterTeacher = async () => {
        if (!regForm.firstName || !regForm.lastName || !regForm.nic || !regForm.teacherTypeId) {
            setRegError('Please fill in all required fields (First Name, Last Name, NIC, Teacher Type)');
            return;
        }
        
        setRegLoading(true);
        setRegResult(null);
        setRegError(null);
        
        try {
            const token = localStorage.getItem('adminToken');
            const response = await fetch(`${API_BASE_URL}/teacher/register`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${token}`
                },
                body: JSON.stringify({
                    ...regForm,
                    dob: regForm.dob ? formatDate(regForm.dob) : null
                })
            });
            
            const data = await response.json();
            
            if (data.success) {
                setRegResult(data);
                setRegForm({
                    firstName: '', lastName: '', nic: '', title: 'Mr.',
                    address1: '', address2: '', address3: '',
                    mobile: '', whatsapp: '', email: '', dob: null, teacherTypeId: null
                });
            } else {
                setRegError(data.message || 'Registration failed');
            }
        } catch (err) {
            setRegError('Failed to register teacher. Please try again.');
        } finally {
            setRegLoading(false);
        }
    };

    const formatDate = (date) => {
        const year = date.getFullYear();
        const month = String(date.getMonth() + 1).padStart(2, '0');
        const day = String(date.getDate()).padStart(2, '0');
        return `${year}-${month}-${day}`;
    };

    const handleSyncAttendance = async (isToday = false) => {
        setSyncLoading(true);
        setSyncResult(null);
        setSyncError(null);

        try {
            const fromDate = isToday ? formatDate(new Date()) : formatDate(dateFrom);
            const toDate = isToday ? formatDate(new Date()) : formatDate(dateTo);

            const token = localStorage.getItem('adminToken');
            const response = await fetch(`${API_BASE_URL}/attendance/sync?from=${fromDate}&to=${toDate}`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${token}`
                },
            });

            const data = await response.json();

            if (response.ok) {
                setSyncResult({
                    success: true,
                    message: data.message || `Successfully synced attendance from ${fromDate} to ${toDate}`,
                    details: data
                });
            } else {
                setSyncError(data.message || 'Failed to sync attendance');
            }
        } catch (err) {
            setSyncError(`Error: ${err.message}`);
        } finally {
            setSyncLoading(false);
        }
    };

    const handlePurgeTid = async () => {
        setPurgeLoading(true);
        setPurgeResult(null);
        setPurgeError(null);

        try {
            const token = localStorage.getItem('adminToken');
            const response = await fetch(`${API_BASE_URL}/teacher/purge-tid`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${token}`
                },
                body: JSON.stringify({ pin: purgePin })
            });

            const data = await response.json();

            if (response.ok && data.success) {
                setPurgeResult(data);
                setPurgeDialogVisible(false);
                setPurgePin('');
            } else {
                setPurgeError(data.message || 'Failed to purge TIDs');
            }
        } catch (err) {
            setPurgeError(`Error: ${err.message}`);
        } finally {
            setPurgeLoading(false);
        }
    };

    // Task definitions
    const tasks = [
        {
            id: 'attendance-sync',
            title: 'Attendance Sync',
            description: 'Fetch fingerprint attendance data from machines',
            icon: 'pi-sync',
            color: '#2266e3',
            bgColor: '#e8f0fe',
            status: 'active'
        },
        {
            id: 'purge-tid',
            title: 'Purge TID',
            description: 'Re-sequence Teacher IDs to fill gaps',
            icon: 'pi-sort-numeric-up',
            color: '#f59e0b',
            bgColor: '#fef3c7',
            status: 'active'
        },
        {
            id: 'teacher-registration',
            title: 'Teacher Registration',
            description: 'Register new teachers to the system',
            icon: 'pi-user-plus',
            color: '#10b981',
            bgColor: '#d1fae5',
            status: 'active'
        },
        {
            id: 'staff-registration',
            title: 'Non-Academic Staff',
            description: 'Register non-academic staff members',
            icon: 'pi-users',
            color: '#8b5cf6',
            bgColor: '#ede9fe',
            status: 'active'
        },
        {
            id: 'download-reports',
            title: 'Download Reports',
            description: 'Monthly & individual attendance reports',
            icon: 'pi-download',
            color: '#059669',
            bgColor: '#d1fae5',
            status: 'active'
        },
        {
            id: 'adjust-attendance',
            title: 'Adjust Attendance',
            description: 'Manually enter missing in/out times',
            icon: 'pi-pencil',
            color: '#ea580c',
            bgColor: '#ffedd5',
            status: 'active'
        },
        {
            id: 'verify-attendance',
            title: 'Verify Entries',
            description: 'Approve or reject manual attendance entries',
            icon: 'pi-check-circle',
            color: '#0891b2',
            bgColor: '#cffafe',
            status: 'active'
        },
        {
            id: 'data-backup',
            title: 'Data Backup',
            description: 'Backup attendance and teacher data',
            icon: 'pi-database',
            color: '#06b6d4',
            bgColor: '#cffafe',
            status: 'coming'
        }
    ];

    const handleTaskClick = (task) => {
        if (task.status === 'coming') return;
        setActiveTask(activeTask === task.id ? null : task.id);
        // Reset states when switching tasks
        setSyncResult(null);
        setSyncError(null);
        setPurgeResult(null);
        setPurgeError(null);
        setSearchQuery('');
        setSearchResults([]);
        setSelectedTeacher(null);
    };

    const renderTaskPanel = () => {
        if (!activeTask) return null;

        switch (activeTask) {
            case 'attendance-sync':
                return (
                    <div className="task-panel">
                        <div className="panel-header">
                            <i className="pi pi-sync" style={{ color: '#2266e3' }}></i>
                            <h3>Attendance Data Sync</h3>
                            <button className="close-btn" onClick={() => setActiveTask(null)}>
                                <i className="pi pi-times"></i>
                            </button>
                        </div>
                        
                        <div className="panel-content">
                            <div className="quick-action">
                                <h4>Quick Sync</h4>
                                <p>Sync today's attendance with a single click</p>
                                <Button 
                                    label="Sync Today" 
                                    icon="pi pi-refresh"
                                    className="p-button-success"
                                    onClick={() => handleSyncAttendance(true)}
                                    disabled={syncLoading}
                                    loading={syncLoading}
                                />
                            </div>

                            <div className="divider"></div>

                            <div className="date-range-section">
                                <h4>Custom Date Range</h4>
                                <div className="date-inputs">
                                    <div className="input-group">
                                        <label>From</label>
                                        <Calendar 
                                            value={dateFrom} 
                                            onChange={(e) => setDateFrom(e.value)}
                                            dateFormat="yy-mm-dd"
                                            showIcon
                                            maxDate={new Date()}
                                        />
                                    </div>
                                    <div className="input-group">
                                        <label>To</label>
                                        <Calendar 
                                            value={dateTo} 
                                            onChange={(e) => setDateTo(e.value)}
                                            dateFormat="yy-mm-dd"
                                            showIcon
                                            maxDate={new Date()}
                                            minDate={dateFrom}
                                        />
                                    </div>
                                    <Button 
                                        label="Sync Range" 
                                        icon="pi pi-download"
                                        className="p-button-primary"
                                        onClick={() => handleSyncAttendance(false)}
                                        disabled={syncLoading}
                                        loading={syncLoading}
                                    />
                                </div>
                            </div>

                            {syncLoading && (
                                <div className="loading-state">
                                    <ProgressSpinner style={{ width: '40px', height: '40px' }} />
                                    <span>Syncing...</span>
                                </div>
                            )}

                            {syncResult && (
                                <Message severity="success" text={syncResult.message} style={{ width: '100%', marginTop: '15px' }} />
                            )}

                            {syncError && (
                                <Message severity="error" text={syncError} style={{ width: '100%', marginTop: '15px' }} />
                            )}

                            <div className="info-box">
                                <i className="pi pi-info-circle"></i>
                                <span>Auto-sync runs daily at 8:00 AM, 1:30 PM, and 4:00 AM</span>
                            </div>
                        </div>
                    </div>
                );

            case 'purge-tid':
                return (
                    <div className="task-panel">
                        <div className="panel-header">
                            <i className="pi pi-sort-numeric-up" style={{ color: '#f59e0b' }}></i>
                            <h3>Purge TID Sequence</h3>
                            <button className="close-btn" onClick={() => setActiveTask(null)}>
                                <i className="pi pi-times"></i>
                            </button>
                        </div>
                        
                        <div className="panel-content">
                            <div className="warning-box">
                                <i className="pi pi-exclamation-triangle"></i>
                                <div>
                                    <strong>What this does:</strong>
                                    <p>Re-sequences Teacher IDs (TID) to fill gaps caused by deleted/transferred teachers while maintaining current order.</p>
                                    <p style={{ marginTop: '8px' }}><strong>Example:</strong> 1, 2, 5, 8, 9 → 1, 2, 3, 4, 5</p>
                                </div>
                            </div>

                            <div className="action-section">
                                <Button 
                                    label="Start TID Purge" 
                                    icon="pi pi-sort-numeric-up"
                                    className="p-button-warning p-button-lg"
                                    onClick={() => { setPurgeDialogVisible(true); setPurgeError(null); }}
                                    disabled={purgeLoading}
                                />
                            </div>

                            {purgeResult && (
                                <div className="result-box success">
                                    <Message severity="success" text={purgeResult.message} style={{ width: '100%' }} />
                                    <div className="result-stats">
                                        <div className="stat">
                                            <span className="label">Total Teachers</span>
                                            <span className="value">{purgeResult.totalTeachers}</span>
                                        </div>
                                        <div className="stat">
                                            <span className="label">TIDs Updated</span>
                                            <span className="value">{purgeResult.updated}</span>
                                        </div>
                                        <div className="stat">
                                            <span className="label">Max TID Before</span>
                                            <span className="value">{purgeResult.beforeMaxTid}</span>
                                        </div>
                                        <div className="stat">
                                            <span className="label">Max TID After</span>
                                            <span className="value">{purgeResult.afterMaxTid}</span>
                                        </div>
                                    </div>
                                </div>
                            )}

                            <div className="info-box">
                                <i className="pi pi-shield"></i>
                                <span>Fingerprint IDs will NOT be changed. Only TID sequence is updated.</span>
                            </div>
                        </div>
                    </div>
                );

            case 'download-reports':
                return (
                    <div className="task-panel">
                        <div className="panel-header">
                            <i className="pi pi-download" style={{ color: '#059669' }}></i>
                            <h3>Download Reports</h3>
                            <button className="close-btn" onClick={() => setActiveTask(null)}>
                                <i className="pi pi-times"></i>
                            </button>
                        </div>
                        
                        <div className="panel-content">
                            <TabView>
                                <TabPanel header="Monthly Report" leftIcon="pi pi-calendar mr-2">
                                    <div className="report-section">
                                        <p>Download monthly attendance report for all teachers or filtered by type.</p>
                                        
                                        <div className="report-form">
                                            <div className="input-group">
                                                <label>Month</label>
                                                <Calendar 
                                                    value={reportMonth} 
                                                    onChange={(e) => setReportMonth(e.value)}
                                                    view="month"
                                                    dateFormat="MM yy"
                                                    showIcon
                                                />
                                            </div>
                                            <div className="input-group">
                                                <label>Teacher Type</label>
                                                <Dropdown 
                                                    value={selectedTeacherType}
                                                    onChange={(e) => setSelectedTeacherType(e.value)}
                                                    options={teacherTypes}
                                                    optionLabel="name"
                                                    optionValue="id"
                                                    placeholder="All Types"
                                                    className="w-full"
                                                />
                                            </div>
                                        </div>
                                        
                                        <Button 
                                            label="Generate Report" 
                                            icon="pi pi-search"
                                            className="p-button-success mt-3"
                                            onClick={handlePreviewMonthlyReport}
                                            disabled={reportLoading}
                                            loading={reportLoading}
                                        />
                                    </div>
                                </TabPanel>
                                
                                <TabPanel header="Individual Report" leftIcon="pi pi-user mr-2">
                                    <div className="report-section">
                                        <p>Search for a teacher by TID, NIC, or name to download their attendance report.</p>
                                        
                                        <div className="search-section">
                                            <div className="input-group" style={{ width: '100%' }}>
                                                <label>Search Teacher</label>
                                                <span className="p-input-icon-left" style={{ width: '100%' }}>
                                                    <i className="pi pi-search" />
                                                    <InputText 
                                                        value={searchQuery}
                                                        onChange={(e) => handleSearchChange(e.target.value)}
                                                        placeholder="Enter TID, NIC or name..."
                                                        style={{ width: '100%' }}
                                                    />
                                                </span>
                                            </div>
                                            
                                            {searchLoading && (
                                                <div className="search-loading">
                                                    <ProgressSpinner style={{ width: '24px', height: '24px' }} />
                                                    <span>Searching...</span>
                                                </div>
                                            )}
                                            
                                            {searchResults.length > 0 && !selectedTeacher && (
                                                <div className="search-results">
                                                    <DataTable 
                                                        value={searchResults} 
                                                        selectionMode="single"
                                                        selection={selectedTeacher}
                                                        onSelectionChange={(e) => setSelectedTeacher(e.value)}
                                                        size="small"
                                                        scrollable
                                                        scrollHeight="200px"
                                                    >
                                                        <Column field="tid" header="Teacher Sign ID" style={{ width: '120px' }} />
                                                        <Column field="name" header="Name" />
                                                        <Column field="nic" header="NIC" style={{ width: '120px' }} />
                                                    </DataTable>
                                                </div>
                                            )}
                                            
                                            {selectedTeacher && (
                                                <div className="selected-teacher">
                                                    <div className="teacher-badge">
                                                        <i className="pi pi-user"></i>
                                                        <div>
                                                            <strong>{selectedTeacher.name}</strong>
                                                            <span>TID: {selectedTeacher.tid} | NIC: {selectedTeacher.nic}</span>
                                                        </div>
                                                        <button onClick={() => setSelectedTeacher(null)}>
                                                            <i className="pi pi-times"></i>
                                                        </button>
                                                    </div>
                                                </div>
                                            )}
                                        </div>
                                        
                                        <div className="report-form mt-3">
                                            <div className="input-group">
                                                <label>Month</label>
                                                <Calendar 
                                                    value={reportMonth} 
                                                    onChange={(e) => setReportMonth(e.value)}
                                                    view="month"
                                                    dateFormat="MM yy"
                                                    showIcon
                                                />
                                            </div>
                                        </div>
                                        
                                        <Button 
                                            label="Generate Report" 
                                            icon="pi pi-search"
                                            className="p-button-success mt-3"
                                            onClick={handlePreviewIndividualReport}
                                            disabled={reportLoading || !selectedTeacher}
                                            loading={reportLoading}
                                        />
                                    </div>
                                </TabPanel>
                            </TabView>
                        </div>
                    </div>
                );

            case 'teacher-registration':
                return (
                    <div className="task-panel">
                        <div className="panel-header">
                            <i className="pi pi-user-plus" style={{ color: '#10b981' }}></i>
                            <h3>Teacher Registration</h3>
                            <button className="close-btn" onClick={() => setActiveTask(null)}>
                                <i className="pi pi-times"></i>
                            </button>
                        </div>
                        
                        <div className="panel-content">
                            <Message 
                                severity="info" 
                                style={{ width: '100%', marginBottom: '20px' }}
                                content={
                                    <div style={{ fontSize: '0.95rem', lineHeight: '1.6' }}>
                                        <strong>Teacher Registration Moved</strong>
                                        <p style={{ marginTop: '10px', marginBottom: '0' }}>
                                            Teacher registration has been moved to the <strong>Teacher Management</strong> page to ensure proper validation of fingerprint machine emp_id against WDMS.
                                        </p>
                                        <p style={{ marginTop: '10px', marginBottom: '0' }}>
                                            This prevents data inconsistency by validating that the enrolment ID exists on the fingerprint machine before registration.
                                        </p>
                                    </div>
                                }
                            />
                            
                            <div style={{ textAlign: 'center', padding: '40px 20px' }}>
                                <i className="pi pi-arrow-right" style={{ fontSize: '2rem', color: '#10b981', marginBottom: '20px' }}></i>
                                <Button
                                    label="Go to Teacher Management"
                                    icon="pi pi-users"
                                    className="p-button-success p-button-lg"
                                    onClick={() => window.location.href = '/admin/dashboard'}
                                    style={{ fontSize: '1.1rem', padding: '12px 30px' }}
                                />
                                <p style={{ marginTop: '20px', color: '#666', fontSize: '0.9rem' }}>
                                    Navigate to Teacher Management → Add Teacher
                                </p>
                            </div>
                        </div>
                    </div>
                );

            case 'staff-registration':
                return (
                    <div className="task-panel">
                        <div className="panel-header">
                            <i className="pi pi-users" style={{ color: '#8b5cf6' }}></i>
                            <h3>Non-Academic Staff Registration</h3>
                            <button className="close-btn" onClick={() => setActiveTask(null)}>
                                <i className="pi pi-times"></i>
                            </button>
                        </div>
                        
                        <div className="panel-content">
                            {staffResult && (
                                <Message 
                                    severity="success" 
                                    text={`Staff registered successfully! Employee No: ${staffResult.employeeNo}`}
                                    style={{ width: '100%', marginBottom: '16px' }}
                                />
                            )}
                            {staffError && (
                                <Message 
                                    severity="error" 
                                    text={staffError}
                                    style={{ width: '100%', marginBottom: '16px' }}
                                />
                            )}
                            
                            <div className="registration-form">
                                <div className="form-row">
                                    <div className="input-group">
                                        <label>Title</label>
                                        <Dropdown
                                            value={staffForm.title}
                                            options={[
                                                { label: 'Mr.', value: 'Mr.' },
                                                { label: 'Mrs.', value: 'Mrs.' },
                                                { label: 'Ms.', value: 'Ms.' },
                                                { label: 'Rev.', value: 'Rev.' }
                                            ]}
                                            onChange={(e) => setStaffForm({...staffForm, title: e.value})}
                                            style={{ width: '100%' }}
                                        />
                                    </div>
                                    <div className="input-group">
                                        <label>First Name *</label>
                                        <InputText
                                            value={staffForm.firstName}
                                            onChange={(e) => setStaffForm({...staffForm, firstName: e.target.value})}
                                            placeholder="First name"
                                            style={{ width: '100%' }}
                                        />
                                    </div>
                                    <div className="input-group">
                                        <label>Last Name *</label>
                                        <InputText
                                            value={staffForm.lastName}
                                            onChange={(e) => setStaffForm({...staffForm, lastName: e.target.value})}
                                            placeholder="Last name"
                                            style={{ width: '100%' }}
                                        />
                                    </div>
                                </div>
                                
                                <div className="form-row">
                                    <div className="input-group">
                                        <label>NIC *</label>
                                        <InputText
                                            value={staffForm.nic}
                                            onChange={(e) => setStaffForm({...staffForm, nic: e.target.value})}
                                            placeholder="National ID"
                                            style={{ width: '100%' }}
                                        />
                                    </div>
                                    <div className="input-group">
                                        <label>Designation</label>
                                        <InputText
                                            value={staffForm.designation}
                                            onChange={(e) => setStaffForm({...staffForm, designation: e.target.value})}
                                            placeholder="e.g., Clerk, Labourer"
                                            style={{ width: '100%' }}
                                        />
                                    </div>
                                    <div className="input-group">
                                        <label>EPF Number</label>
                                        <InputText
                                            value={staffForm.epfNo}
                                            onChange={(e) => setStaffForm({...staffForm, epfNo: e.target.value})}
                                            placeholder="EPF No"
                                            style={{ width: '100%' }}
                                        />
                                    </div>
                                </div>
                                
                                <div className="form-row">
                                    <div className="input-group">
                                        <label>Date of Birth</label>
                                        <Calendar
                                            value={staffForm.dob}
                                            onChange={(e) => setStaffForm({...staffForm, dob: e.value})}
                                            dateFormat="yy-mm-dd"
                                            showIcon
                                            maxDate={new Date()}
                                            style={{ width: '100%' }}
                                        />
                                    </div>
                                    <div className="input-group">
                                        <label>Mobile Phone</label>
                                        <InputText
                                            value={staffForm.mobile}
                                            onChange={(e) => setStaffForm({...staffForm, mobile: e.target.value})}
                                            placeholder="07X XXX XXXX"
                                            style={{ width: '100%' }}
                                        />
                                    </div>
                                    <div className="input-group">
                                        <label>WhatsApp Number</label>
                                        <InputText
                                            value={staffForm.whatsapp}
                                            onChange={(e) => setStaffForm({...staffForm, whatsapp: e.target.value})}
                                            placeholder="07X XXX XXXX"
                                            style={{ width: '100%' }}
                                        />
                                    </div>
                                </div>
                                
                                <div className="form-row">
                                    <div className="input-group">
                                        <label>Email</label>
                                        <InputText
                                            value={staffForm.email}
                                            onChange={(e) => setStaffForm({...staffForm, email: e.target.value})}
                                            placeholder="email@example.com"
                                            style={{ width: '100%' }}
                                        />
                                    </div>
                                </div>
                                
                                <div className="form-row">
                                    <div className="input-group" style={{ flex: 2 }}>
                                        <label>Address Line 1</label>
                                        <InputText
                                            value={staffForm.address1}
                                            onChange={(e) => setStaffForm({...staffForm, address1: e.target.value})}
                                            placeholder="Street address"
                                            style={{ width: '100%' }}
                                        />
                                    </div>
                                </div>
                                
                                <div className="form-row">
                                    <div className="input-group">
                                        <label>Address Line 2</label>
                                        <InputText
                                            value={staffForm.address2}
                                            onChange={(e) => setStaffForm({...staffForm, address2: e.target.value})}
                                            placeholder="City"
                                            style={{ width: '100%' }}
                                        />
                                    </div>
                                    <div className="input-group">
                                        <label>Address Line 3</label>
                                        <InputText
                                            value={staffForm.address3}
                                            onChange={(e) => setStaffForm({...staffForm, address3: e.target.value})}
                                            placeholder="District"
                                            style={{ width: '100%' }}
                                        />
                                    </div>
                                </div>
                                
                                <div className="form-actions">
                                    <Button
                                        label="Register Staff"
                                        icon="pi pi-users"
                                        className="p-button-help"
                                        onClick={handleRegisterStaff}
                                        loading={staffLoading}
                                        disabled={staffLoading}
                                    />
                                </div>
                            </div>
                        </div>
                    </div>
                );

            case 'adjust-attendance':
                return (
                    <div className="task-panel">
                        <div className="panel-header">
                            <i className="pi pi-pencil" style={{ color: '#ea580c' }}></i>
                            <h3>Adjust Attendance</h3>
                            <button className="close-btn" onClick={() => setActiveTask(null)}>
                                <i className="pi pi-times"></i>
                            </button>
                        </div>
                        
                        <div className="panel-content">
                            {adjustResult && (
                                <Message 
                                    severity="success" 
                                    text="Attendance entry saved. Pending verification."
                                    style={{ width: '100%', marginBottom: '16px' }}
                                />
                            )}
                            {adjustError && (
                                <Message 
                                    severity="error" 
                                    text={adjustError}
                                    style={{ width: '100%', marginBottom: '16px' }}
                                />
                            )}
                            
                            <div className="registration-form">
                                <div className="form-row">
                                    <div className="input-group" style={{ flex: 2 }}>
                                        <label>Search Teacher *</label>
                                        <div style={{ position: 'relative' }}>
                                            <InputText
                                                value={adjustTeacherSearch}
                                                onChange={(e) => handleAdjustTeacherSearch(e.target.value)}
                                                placeholder="Search by TID, Name, or NIC"
                                                style={{ width: '100%' }}
                                            />
                                            {adjustTeacherResults.length > 0 && (
                                                <div style={{
                                                    position: 'absolute', top: '100%', left: 0, right: 0,
                                                    background: 'white', border: '1px solid #ddd', borderRadius: '4px',
                                                    maxHeight: '200px', overflow: 'auto', zIndex: 100, boxShadow: '0 4px 12px rgba(0,0,0,0.15)'
                                                }}>
                                                    {adjustTeacherResults.map(t => (
                                                        <div key={t.id} 
                                                            style={{ padding: '10px', cursor: 'pointer', borderBottom: '1px solid #eee' }}
                                                            onClick={() => {
                                                                setSelectedAdjustTeacher(t);
                                                                setAdjustTeacherSearch(`${t.tid} - ${t.name}`);
                                                                setAdjustTeacherResults([]);
                                                            }}
                                                        >
                                                            <strong>TID {t.tid}</strong> - {t.name}
                                                            <div style={{ fontSize: '0.85rem', color: '#666' }}>{t.nic}</div>
                                                        </div>
                                                    ))}
                                                </div>
                                            )}
                                        </div>
                                    </div>
                                </div>
                                
                                {selectedAdjustTeacher && (
                                    <div style={{ padding: '10px', background: '#f0f9ff', borderRadius: '8px', marginBottom: '16px' }}>
                                        <strong>Selected:</strong> TID {selectedAdjustTeacher.tid} - {selectedAdjustTeacher.name}
                                    </div>
                                )}
                                
                                <div className="form-row">
                                    <div className="input-group">
                                        <label>Date *</label>
                                        <Calendar
                                            value={adjustForm.date}
                                            onChange={(e) => setAdjustForm({...adjustForm, date: e.value})}
                                            dateFormat="yy-mm-dd"
                                            showIcon
                                            maxDate={new Date()}
                                            style={{ width: '100%' }}
                                        />
                                    </div>
                                    <div className="input-group">
                                        <label>In Time</label>
                                        <InputText
                                            value={adjustForm.inTime}
                                            onChange={(e) => setAdjustForm({...adjustForm, inTime: e.target.value})}
                                            placeholder="HH:MM (e.g., 08:30)"
                                            style={{ width: '100%' }}
                                        />
                                    </div>
                                    <div className="input-group">
                                        <label>Out Time</label>
                                        <InputText
                                            value={adjustForm.outTime}
                                            onChange={(e) => setAdjustForm({...adjustForm, outTime: e.target.value})}
                                            placeholder="HH:MM (e.g., 16:30)"
                                            style={{ width: '100%' }}
                                        />
                                    </div>
                                </div>
                                
                                <div className="form-row">
                                    <div className="input-group" style={{ flex: 2 }}>
                                        <label>Remarks</label>
                                        <InputText
                                            value={adjustForm.remarks}
                                            onChange={(e) => setAdjustForm({...adjustForm, remarks: e.target.value})}
                                            placeholder="Reason for manual entry"
                                            style={{ width: '100%' }}
                                        />
                                    </div>
                                </div>
                                
                                <div className="form-actions">
                                    <Button
                                        label="Save Entry"
                                        icon="pi pi-save"
                                        className="p-button-warning"
                                        onClick={handleAdjustAttendance}
                                        loading={adjustLoading}
                                        disabled={adjustLoading}
                                    />
                                </div>
                                
                                <div className="info-box" style={{ marginTop: '20px' }}>
                                    <i className="pi pi-info-circle"></i>
                                    <span>Manual entries require supervisor verification before appearing in reports.</span>
                                </div>
                            </div>
                        </div>
                    </div>
                );

            case 'verify-attendance':
                return (
                    <div className="task-panel">
                        <div className="panel-header">
                            <i className="pi pi-check-circle" style={{ color: '#0891b2' }}></i>
                            <h3>Verify Attendance Entries</h3>
                            <button className="close-btn" onClick={() => setActiveTask(null)}>
                                <i className="pi pi-times"></i>
                            </button>
                        </div>
                        
                        <div className="panel-content">
                            {verifyError && (
                                <Message 
                                    severity="error" 
                                    text={verifyError}
                                    style={{ width: '100%', marginBottom: '16px' }}
                                />
                            )}
                            
                            <div style={{ marginBottom: '16px' }}>
                                <Button
                                    label="Refresh"
                                    icon="pi pi-refresh"
                                    className="p-button-outlined"
                                    onClick={fetchPendingEntries}
                                    loading={verifyLoading}
                                />
                            </div>
                            
                            {pendingEntries.length === 0 ? (
                                <div style={{ textAlign: 'center', padding: '40px', color: '#6b7280' }}>
                                    <i className="pi pi-check-circle" style={{ fontSize: '3rem', color: '#10b981', marginBottom: '16px', display: 'block' }}></i>
                                    <p>No pending entries to verify</p>
                                </div>
                            ) : (
                                <DataTable value={pendingEntries} loading={verifyLoading} responsiveLayout="scroll">
                                    <Column field="tid" header="Teacher Sign ID" style={{ width: '140px' }} />
                                    <Column field="teacherName" header="Teacher" />
                                    <Column field="date" header="Date" style={{ width: '120px' }} />
                                    <Column field="inTime" header="In" style={{ width: '80px' }} body={(row) => row.inTime || '-'} />
                                    <Column field="outTime" header="Out" style={{ width: '80px' }} body={(row) => row.outTime || '-'} />
                                    <Column field="enteredBy" header="Entered By" style={{ width: '120px' }} />
                                    <Column field="remarks" header="Remarks" />
                                    <Column header="Action" style={{ width: '180px' }} body={(row) => (
                                        <div style={{ display: 'flex', gap: '8px' }}>
                                            <Button
                                                icon="pi pi-check"
                                                className="p-button-success p-button-sm"
                                                tooltip="Approve"
                                                onClick={() => handleVerifyEntry(row.id, 'approve')}
                                            />
                                            <Button
                                                icon="pi pi-times"
                                                className="p-button-danger p-button-sm"
                                                tooltip="Reject"
                                                onClick={() => handleVerifyEntry(row.id, 'reject')}
                                            />
                                        </div>
                                    )} />
                                </DataTable>
                            )}
                        </div>
                    </div>
                );

            default:
                return null;
        }
    };

    return (
        <div className="task-management-modern">
            <div className="task-layout">
                <div className="tasks-sidebar">
                    {tasks.map(task => (
                        <div 
                            key={task.id}
                            className={`task-card ${task.status === 'coming' ? 'coming-soon' : ''} ${activeTask === task.id ? 'active' : ''}`}
                            onClick={() => handleTaskClick(task)}
                        >
                            <div className="task-icon" style={{ backgroundColor: task.bgColor }}>
                                <i className={`pi ${task.icon}`} style={{ color: task.color }}></i>
                            </div>
                            <div className="task-info">
                                <h3>{task.title}</h3>
                                <p>{task.description}</p>
                            </div>
                            {task.status === 'coming' && (
                                <span className="coming-badge">Coming Soon</span>
                            )}
                            {task.status === 'active' && (
                                <i className="pi pi-chevron-right arrow-icon"></i>
                            )}
                        </div>
                    ))}
                </div>

                <div className="task-content">
                    {renderTaskPanel()}
                </div>
            </div>

            <Dialog
                header="Confirm TID Purge"
                visible={purgeDialogVisible}
                style={{ width: '400px' }}
                onHide={() => { setPurgeDialogVisible(false); setPurgePin(''); setPurgeError(null); }}
                footer={
                    <div>
                        <Button 
                            label="Cancel" 
                            icon="pi pi-times" 
                            className="p-button-text" 
                            onClick={() => { setPurgeDialogVisible(false); setPurgePin(''); setPurgeError(null); }} 
                        />
                        <Button 
                            label="Confirm Purge" 
                            icon="pi pi-check" 
                            className="p-button-warning" 
                            onClick={handlePurgeTid}
                            disabled={purgeLoading || !purgePin}
                            loading={purgeLoading}
                        />
                    </div>
                }
            >
                <div className="purge-dialog-content">
                    <p><i className="pi pi-exclamation-triangle" style={{ color: '#f59e0b', marginRight: '8px' }}></i>
                        This will re-sequence all Teacher IDs to fill gaps.</p>
                    <div style={{ marginTop: '20px' }}>
                        <label htmlFor="purgePin" style={{ display: 'block', marginBottom: '8px', fontWeight: '600' }}>
                            Enter PIN to confirm:
                        </label>
                        <InputText
                            id="purgePin"
                            type="password"
                            value={purgePin}
                            onChange={(e) => setPurgePin(e.target.value)}
                            placeholder="Enter PIN"
                            style={{ width: '100%' }}
                        />
                    </div>
                    {purgeError && (
                        <Message 
                            severity="error" 
                            text={purgeError}
                            style={{ width: '100%', marginTop: '15px' }}
                        />
                    )}
                </div>
            </Dialog>

            {/* Report Preview Dialog */}
            <Dialog
                header={previewTitle}
                visible={previewVisible}
                style={{ width: '90vw', maxWidth: '1000px' }}
                onHide={() => setPreviewVisible(false)}
                footer={
                    <div style={{ display: 'flex', gap: '8px', justifyContent: 'flex-end' }}>
                        <Button 
                            label="Close" 
                            icon="pi pi-times" 
                            className="p-button-text" 
                            onClick={() => setPreviewVisible(false)} 
                        />
                        <Button 
                            label="Download PDF" 
                            icon="pi pi-file-pdf" 
                            className="p-button-danger"
                            onClick={() => {
                                handleDownloadMonthlyPDF();
                            }} 
                        />
                        <Button 
                            label="Download Excel" 
                            icon="pi pi-file-excel" 
                            className="p-button-success"
                            onClick={() => {
                                handleDownloadMonthlyReport();
                            }} 
                        />
                    </div>
                }
            >
                <div className="preview-summary" style={{ marginBottom: '16px', display: 'flex', gap: '20px' }}>
                    <span><strong>Total Teachers:</strong> {previewData.length}</span>
                    <span><strong>Present Total:</strong> {previewData.reduce((sum, r) => sum + (r.presentDays || 0), 0)}</span>
                </div>
                <DataTable 
                    value={previewData} 
                    scrollable 
                    scrollHeight="400px"
                    size="small"
                    stripedRows
                >
                    <Column field="tid" header="Teacher Sign ID" style={{ width: '140px' }} sortable />
                    <Column field="name" header="Name" sortable />
                    <Column field="teacherType" header="Type" style={{ width: '120px' }} sortable />
                    <Column field="nic" header="NIC" style={{ width: '120px' }} />
                    <Column field="school" header="School" style={{ width: '120px' }} />
                    <Column field="punches" header="Punches" style={{ width: '90px' }} sortable />
                    <Column field="workingDays" header="Working" style={{ width: '70px', textAlign: 'center' }} sortable />
                    <Column field="presentDays" header="Present" style={{ width: '70px', textAlign: 'center' }} sortable />
                    <Column field="absentDays" header="Absent" style={{ width: '70px', textAlign: 'center' }} sortable />
                </DataTable>
            </Dialog>

            {/* Individual Report Preview Dialog */}
            <Dialog
                header={individualTeacherInfo ? `${individualTeacherInfo.name} - Attendance Report` : 'Individual Report'}
                visible={individualPreviewVisible}
                style={{ width: '90vw', maxWidth: '800px' }}
                onHide={() => setIndividualPreviewVisible(false)}
                footer={
                    <div>
                        <Button 
                            label="Close" 
                            icon="pi pi-times" 
                            className="p-button-text" 
                            onClick={() => setIndividualPreviewVisible(false)} 
                        />
                        <Button 
                            label="Download PDF" 
                            icon="pi pi-file-pdf" 
                            className="p-button-danger"
                            onClick={() => {
                                handleDownloadIndividualPDF();
                            }} 
                        />
                        <Button 
                            label="Download Excel" 
                            icon="pi pi-file-excel" 
                            className="p-button-success"
                            onClick={() => {
                                handleDownloadIndividualReport();
                            }} 
                        />
                    </div>
                }
            >
                {individualTeacherInfo && (
                    <div className="individual-report-header" style={{ 
                        marginBottom: '16px', 
                        padding: '12px', 
                        background: '#f8fafc', 
                        borderRadius: '8px',
                        display: 'grid',
                        gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))',
                        gap: '12px'
                    }}>
                        <div><strong>TID:</strong> {individualTeacherInfo.tid}</div>
                        <div><strong>NIC:</strong> {individualTeacherInfo.nic || 'N/A'}</div>
                        <div><strong>Type:</strong> {individualTeacherInfo.teacherType || 'N/A'}</div>
                        <div><strong>Month:</strong> {reportMonth.toLocaleString('default', { month: 'long', year: 'numeric' })}</div>
                    </div>
                )}
                <div className="preview-summary" style={{ marginBottom: '16px', display: 'flex', gap: '20px' }}>
                    <span><strong>Total Days:</strong> {individualPreviewData.length}</span>
                    <span><strong>Present:</strong> {individualPreviewData.filter(r => r.inTime).length}</span>
                    <span><strong>Absent:</strong> {individualPreviewData.filter(r => !r.inTime).length}</span>
                </div>
                <DataTable 
                    value={individualPreviewData} 
                    scrollable 
                    scrollHeight="350px"
                    size="small"
                    stripedRows
                    rowClassName={(data) => {
                        if (data.dayName === 'Saturday' || data.dayName === 'Sunday') return 'weekend-row';
                        if (!data.inTime) return 'absent-row';
                        return '';
                    }}
                >
                    <Column field="date" header="Date" style={{ width: '110px' }} />
                    <Column field="dayName" header="Day" style={{ width: '100px' }} />
                    <Column field="inTime" header="In Time" style={{ width: '100px' }} body={(row) => row.inTime || '-'} />
                    <Column field="outTime" header="Out Time" style={{ width: '100px' }} body={(row) => row.outTime || '-'} />
                </DataTable>
            </Dialog>

            {/* Generating Progress Dialog */}
            <Dialog
                visible={generatingVisible}
                style={{ width: '350px' }}
                closable={false}
                showHeader={false}
                modal
            >
                <div style={{ 
                    display: 'flex', 
                    flexDirection: 'column', 
                    alignItems: 'center', 
                    padding: '30px 20px',
                    gap: '20px'
                }}>
                    <ProgressSpinner style={{ width: '50px', height: '50px' }} />
                    <div style={{ textAlign: 'center' }}>
                        <h4 style={{ margin: '0 0 8px 0', color: '#1f2937' }}>Generating Report</h4>
                        <p style={{ margin: 0, color: '#6b7280', fontSize: '0.9rem' }}>
                            Please wait while fetching attendance data...
                        </p>
                    </div>
                </div>
            </Dialog>

            <style>{`
                .task-management-modern {
                    padding: 16px 16px 16px 0;
                    max-width: 1400px;
                }

                .task-layout {
                    display: flex;
                    gap: 16px;
                    align-items: flex-start;
                }

                .tasks-sidebar {
                    width: 320px;
                    flex-shrink: 0;
                    display: flex;
                    flex-direction: column;
                    gap: 12px;
                }

                .task-content {
                    flex: 1;
                    min-width: 0;
                }

                .page-header {
                    margin-bottom: 30px;
                }

                .page-header h1 {
                    display: flex;
                    align-items: center;
                    gap: 12px;
                    margin: 0 0 8px 0;
                    color: #1e3a5f;
                    font-size: 1.75rem;
                }

                .page-header p {
                    margin: 0;
                    color: #6b7280;
                    font-size: 1rem;
                }

                .tasks-grid {
                    display: grid;
                    grid-template-columns: repeat(auto-fill, minmax(280px, 1fr));
                    gap: 20px;
                    margin-bottom: 24px;
                }

                .task-card {
                    background: white;
                    border-radius: 12px;
                    padding: 20px;
                    display: flex;
                    align-items: center;
                    gap: 16px;
                    cursor: pointer;
                    transition: all 0.2s ease;
                    border: 2px solid transparent;
                    box-shadow: 0 2px 8px rgba(0,0,0,0.06);
                    position: relative;
                }

                .task-card:hover:not(.coming-soon) {
                    transform: translateY(-2px);
                    box-shadow: 0 8px 20px rgba(0,0,0,0.1);
                    border-color: #2266e3;
                }

                .task-card.active {
                    border-color: #2266e3;
                    background: #f8faff;
                }

                .task-card.coming-soon {
                    opacity: 0.7;
                    cursor: not-allowed;
                }

                .task-icon {
                    width: 56px;
                    height: 56px;
                    border-radius: 12px;
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    flex-shrink: 0;
                }

                .task-icon i {
                    font-size: 1.5rem;
                }

                .task-info {
                    flex: 1;
                }

                .task-info h3 {
                    margin: 0 0 4px 0;
                    font-size: 1rem;
                    font-weight: 600;
                    color: #1f2937;
                }

                .task-info p {
                    margin: 0;
                    font-size: 0.85rem;
                    color: #6b7280;
                }

                .coming-badge {
                    position: absolute;
                    top: 12px;
                    right: 12px;
                    background: #e5e7eb;
                    color: #6b7280;
                    font-size: 0.7rem;
                    padding: 4px 8px;
                    border-radius: 12px;
                    font-weight: 600;
                }

                .arrow-icon {
                    color: #9ca3af;
                    font-size: 0.9rem;
                }

                .task-panel {
                    background: white;
                    border-radius: 12px;
                    box-shadow: 0 4px 16px rgba(0,0,0,0.08);
                    overflow: hidden;
                    animation: slideDown 0.2s ease;
                }

                @keyframes slideDown {
                    from { opacity: 0; transform: translateY(-10px); }
                    to { opacity: 1; transform: translateY(0); }
                }

                .panel-header {
                    display: flex;
                    align-items: center;
                    gap: 12px;
                    padding: 20px 24px;
                    background: linear-gradient(135deg, #f8faff 0%, #fff 100%);
                    border-bottom: 1px solid #e5e7eb;
                }

                .panel-header i:first-child {
                    font-size: 1.5rem;
                }

                .panel-header h3 {
                    flex: 1;
                    margin: 0;
                    font-size: 1.25rem;
                    color: #1f2937;
                }

                .close-btn {
                    background: none;
                    border: none;
                    width: 32px;
                    height: 32px;
                    border-radius: 8px;
                    cursor: pointer;
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    color: #6b7280;
                    transition: all 0.2s;
                }

                .close-btn:hover {
                    background: #f3f4f6;
                    color: #1f2937;
                }

                .panel-content {
                    padding: 24px;
                }

                .quick-action, .date-range-section, .action-section {
                    margin-bottom: 20px;
                }

                .quick-action h4, .date-range-section h4 {
                    margin: 0 0 8px 0;
                    font-size: 1rem;
                    color: #374151;
                }

                .quick-action p, .date-range-section p {
                    margin: 0 0 12px 0;
                    color: #6b7280;
                    font-size: 0.9rem;
                }

                .divider {
                    height: 1px;
                    background: #e5e7eb;
                    margin: 20px 0;
                }

                .date-inputs {
                    display: flex;
                    flex-wrap: wrap;
                    gap: 16px;
                    align-items: flex-end;
                }

                .input-group {
                    display: flex;
                    flex-direction: column;
                    gap: 6px;
                }

                .input-group label {
                    font-size: 0.85rem;
                    font-weight: 600;
                    color: #4b5563;
                }

                .loading-state {
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    gap: 12px;
                    padding: 20px;
                    color: #6b7280;
                }

                .warning-box {
                    display: flex;
                    gap: 12px;
                    padding: 16px;
                    background: #fef3c7;
                    border-radius: 8px;
                    margin-bottom: 20px;
                }

                .warning-box i {
                    color: #f59e0b;
                    font-size: 1.25rem;
                    flex-shrink: 0;
                    margin-top: 2px;
                }

                .warning-box p {
                    margin: 4px 0 0 0;
                    color: #92400e;
                    font-size: 0.9rem;
                }

                .info-box {
                    display: flex;
                    align-items: center;
                    gap: 10px;
                    padding: 12px 16px;
                    background: #f0f7ff;
                    border-radius: 8px;
                    margin-top: 20px;
                    font-size: 0.85rem;
                    color: #1e40af;
                }

                .info-box i {
                    color: #2266e3;
                }

                .result-box {
                    margin-top: 20px;
                }

                .result-stats {
                    display: grid;
                    grid-template-columns: repeat(2, 1fr);
                    gap: 12px;
                    margin-top: 16px;
                    padding: 16px;
                    background: #f8fafc;
                    border-radius: 8px;
                }

                .stat {
                    display: flex;
                    flex-direction: column;
                    gap: 4px;
                }

                .stat .label {
                    font-size: 0.75rem;
                    color: #6b7280;
                    text-transform: uppercase;
                    letter-spacing: 0.5px;
                }

                .stat .value {
                    font-size: 1.25rem;
                    font-weight: 700;
                    color: #1f2937;
                }

                .report-section {
                    padding: 10px 0;
                }

                .report-section p {
                    color: #6b7280;
                    margin-bottom: 16px;
                }

                .report-form {
                    display: flex;
                    flex-wrap: wrap;
                    gap: 16px;
                    align-items: flex-end;
                }

                .search-section {
                    margin-bottom: 16px;
                }

                .search-loading {
                    display: flex;
                    align-items: center;
                    gap: 8px;
                    padding: 12px;
                    color: #6b7280;
                    font-size: 0.9rem;
                }

                .search-results {
                    margin-top: 12px;
                    border: 1px solid #e5e7eb;
                    border-radius: 8px;
                    overflow: hidden;
                }

                .selected-teacher {
                    margin-top: 12px;
                }

                .teacher-badge {
                    display: flex;
                    align-items: center;
                    gap: 12px;
                    padding: 12px 16px;
                    background: #d1fae5;
                    border-radius: 8px;
                    border: 1px solid #10b981;
                }

                .teacher-badge > i {
                    font-size: 1.5rem;
                    color: #059669;
                }

                .teacher-badge > div {
                    flex: 1;
                    display: flex;
                    flex-direction: column;
                    gap: 2px;
                }

                .teacher-badge > div strong {
                    color: #065f46;
                }

                .teacher-badge > div span {
                    font-size: 0.85rem;
                    color: #047857;
                }

                .teacher-badge > button {
                    background: none;
                    border: none;
                    cursor: pointer;
                    color: #059669;
                    padding: 4px;
                }

                .teacher-badge > button:hover {
                    color: #065f46;
                }

                .mt-3 {
                    margin-top: 16px;
                }

                .registration-form {
                    display: flex;
                    flex-direction: column;
                    gap: 16px;
                }

                .form-row {
                    display: flex;
                    gap: 16px;
                }

                .form-row .input-group {
                    flex: 1;
                }

                .form-actions {
                    margin-top: 8px;
                    padding-top: 16px;
                    border-top: 1px solid #e5e7eb;
                }

                .weekend-row {
                    background-color: #f3f4f6 !important;
                    color: #9ca3af;
                }

                .absent-row {
                    background-color: #fef2f2 !important;
                }

                .absent-row td {
                    color: #dc2626;
                }

                @media (max-width: 768px) {
                    .tasks-grid {
                        grid-template-columns: 1fr;
                    }
                    
                    .date-inputs {
                        flex-direction: column;
                        align-items: stretch;
                    }

                    .date-inputs .p-button {
                        width: 100%;
                    }
                }
            `}</style>
        </div>
    );
}

export default TaskManagement;
